import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import ImageExt from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import { useEffect, useState } from "react";
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link as LinkIcon, Heading1, Heading2, Heading3, Quote, Image as ImageIcon, Pilcrow, Undo2, Redo2 } from "lucide-react";
import MediaPicker from "./MediaPicker";

const baseStyle = "p-2 rounded hover:bg-black/5 disabled:opacity-30 transition-colors";

export default function RichTextEditor({ value, onChange, placeholder, testId = "rich-editor" }) {
  const [showMedia, setShowMedia] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-[#37B160] underline" } }),
      ImageExt.configure({ HTMLAttributes: { class: "rounded-xl my-3" } }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-neutral max-w-none focus:outline-none min-h-[280px] py-3 px-4",
        "data-testid": `${testId}-content`,
      },
    },
  });

  // Sync external value changes (e.g. when opening editor for a new post)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) editor.commands.setContent(value || "", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const promptLink = () => {
    const previous = editor.getAttributes("link").href;
    const url = window.prompt("URL", previous || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertImage = (url) => {
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  const Btn = ({ onClick, active, disabled, title, testId, children }) => (
    <button type="button" onClick={onClick} disabled={disabled} title={title}
            data-testid={testId}
            className={`${baseStyle} ${active ? "bg-black/10 text-[#37B160]" : "text-[#4B5563]"}`}>
      {children}
    </button>
  );

  return (
    <>
      <div className="border border-black/10 rounded-xl overflow-hidden bg-white" data-testid={testId}>
        <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-black/5 bg-[#FAFAFA]">
          <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold" testId="rte-bold"><Bold className="h-4 w-4" /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic" testId="rte-italic"><Italic className="h-4 w-4" /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline" testId="rte-underline"><UnderlineIcon className="h-4 w-4" /></Btn>
          <div className="w-px h-6 bg-black/10 mx-1" />
          <Btn onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive("paragraph")} title="Paragraph" testId="rte-p"><Pilcrow className="h-4 w-4" /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Heading 1" testId="rte-h1"><Heading1 className="h-4 w-4" /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading 2" testId="rte-h2"><Heading2 className="h-4 w-4" /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Heading 3" testId="rte-h3"><Heading3 className="h-4 w-4" /></Btn>
          <div className="w-px h-6 bg-black/10 mx-1" />
          <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullets" testId="rte-bullets"><List className="h-4 w-4" /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered" testId="rte-ordered"><ListOrdered className="h-4 w-4" /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote" testId="rte-quote"><Quote className="h-4 w-4" /></Btn>
          <div className="w-px h-6 bg-black/10 mx-1" />
          <Btn onClick={promptLink} active={editor.isActive("link")} title="Link" testId="rte-link"><LinkIcon className="h-4 w-4" /></Btn>
          <Btn onClick={() => setShowMedia(true)} title="Insert image from media library" testId="rte-image"><ImageIcon className="h-4 w-4" /></Btn>
          <div className="w-px h-6 bg-black/10 mx-1" />
          <Btn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testId="rte-undo"><Undo2 className="h-4 w-4" /></Btn>
          <Btn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testId="rte-redo"><Redo2 className="h-4 w-4" /></Btn>
        </div>
        <EditorContent editor={editor} placeholder={placeholder} />
      </div>
      <MediaPicker
        open={showMedia}
        onClose={() => setShowMedia(false)}
        onPick={(item) => { insertImage(item.url); setShowMedia(false); }}
      />
    </>
  );
}
